import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export interface BrandConfig {
  name: string;
  logo: string;
  logoBg?: string;
  loginTitle: string;
}

interface BrandingConfig {
  default: string;
  brands: { [key: string]: BrandConfig };
}

@Injectable()
export class BrandingService {

  private config: BrandingConfig;

  constructor(private http: HttpClient) {}

  /**
   * Load branding config from JSON. Called via APP_INITIALIZER
   * so config is ready before any component runs.
   */
  load(): Promise<void> {
    return this.http.get<BrandingConfig>('assets/config/branding.json')
      .toPromise()
      .then(data => {
        this.config = data;
      });
  }

  /**
   * Check if a brand slug is a valid registered operator or default route.
   */
  isValidBrand(slug: string): boolean {
    if (!slug) return false;
    const lower = slug.toLowerCase().trim();
    if (lower === 'login' || lower === 'roadgrid') return true;
    if (this.config && this.config.brands) {
      const normalized = lower.replace(/[-_ ]/g, '');
      return !!(this.config.brands[slug] || this.config.brands[lower] || this.config.brands[normalized]);
    }
    const known = ['nexa', 'nexusvivita', 'nexus-vivita', 'vvita', 'roadgrid'];
    return known.includes(lower) || known.includes(lower.replace(/[-_ ]/g, ''));
  }

  /**
   * Get brand config by slug (e.g., 'nexa', 'roadgrid').
   * Returns null if the slug is not a registered brand.
   */
  getBrand(slug: string): BrandConfig {
    if (!slug) {
      return this.getDefaultBrand();
    }
    if (this.config && this.config.brands) {
      const lower = slug.toLowerCase().trim();
      const normalized = lower.replace(/[-_ ]/g, '');
      const brand = this.config.brands[slug] || this.config.brands[lower] || this.config.brands[normalized];
      if (brand) {
        return brand;
      }
    }
    return null;
  }

  /**
   * Get the default brand config (roadgrid).
   */
  getDefaultBrand(): BrandConfig {
    if (this.config && this.config.brands) {
      return this.config.brands[this.config.default];
    }
    // Hardcoded fallback
    return {
      name: 'RoadGrid',
      logo: 'assets/images/Roadgrid.png',
      logoBg: 'transparent',
      loginTitle: 'RoadGrid CMS'
    };
  }
}
